import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Button, Col, notification, Select, Input, Modal, Row } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import './PaymentForm.css';
import { getRequest, postRequest } from '../../api';
import PopUpInfo from '../PopUpInfo/PopUpInfo';
import { getOnboardingDetails, updateUser, createNewSubscription } from '../../api/password';
import goBackButton from "../../assets/images/Go_Back.svg";
import { styles } from "../../pages/onboarding/PaymentMethodStyles.js";
import MasterCardLogo from "../../assets/images/mastercard-logo.png";
import VisaLogo from "../../assets/images/visa-logo.png";

const { Option } = Select;

const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('free');
  const { subscriptionId, coupon, couponAmount, couponDurationMonths } = location.state || {};
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [defaultCard, setDefaultCard] = useState(null);
  const [isAddingNewCard, setIsAddingNewCard] = useState(null);
  const [cardOwnerName, setCardOwnerName] = useState('');

  localStorage.setItem("plan", location.state.selectedPlan.title)

  // Use planId from selectedPlan if available (from API), otherwise fallback to env variables
  let planId = location.state.selectedPlan.planId || location.state.selectedPlan.stripePlanId;

  // Fallback to environment variables if no planId in selectedPlan
  if (!planId) {
    let title = location.state.selectedPlan.price.trim();
    const planIdMap = {
      'free': process.env.REACT_APP_FREE_PLAN,
      '/month': process.env.REACT_APP_MONTHLY_PLAN,
      '/year': process.env.REACT_APP_YEARLY_PLAN,
    };
    planId = planIdMap[title];
  }

  useEffect(() => {
    const fetchDefaultCard = async () => {
      try {
        const response = await getRequest("/stripe/default-payment-method");
        if (response && response?.defaultPaymentMethod?.id) {
          setDefaultCard(response);
          setCardOwnerName(response?.defaultPaymentMethod?.cardOwnerName);
        }
      } catch (error) {
        console.error("Error fetching default card details:", error);
      }
    };
    fetchDefaultCard();
  }, []);

  useEffect(() => {
    if (location.state && location.state.selectedPlan) {
      setSelectedPlan(location.state.selectedPlan);
    }
  }, [location.state]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    if (country === "") {
      notification.error({
        message: 'Missing Information',
        description: 'Please select a valid country before submitting.',
        duration: 10,
      });
      return;
    }

    const customerEmail = localStorage.getItem("useremail");
    let id = localStorage.getItem("userid");
    const userId = Number(id);

    if (!userId || !customerEmail) {
      notification.error({
        message: "Missing Information",
        description: "User information is missing. Please sign in again.",
        duration: 10,
      });
      return;
    }

    setLoading(true);

    let paymentMethodId;

    if (isAddingNewCard || !defaultCard) {
      const cardElement = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: cardOwnerName,
          email: customerEmail,
        },
      });

      if (error) {
        notification.error({
          message: "Payment Error",
          description: error.message,
          duration: 10,
        });
        setLoading(false);
        return;
      }

      paymentMethodId = paymentMethod.id;
    } else if (defaultCard) {
      paymentMethodId = defaultCard?.defaultPaymentMethod?.id;
    }

    try {
      const { onboarding } = location.state || {};

      let response;

      if (onboarding) {

        response = await postRequest("/stripe/create-subscription", {
          paymentMethodId: paymentMethodId,
          planId: planId,
          coupon: coupon,
          couponAmount: couponAmount,
          couponDurationMonths: couponDurationMonths
        });
      } else {
        response = await createNewSubscription({
          paymentMethodId: paymentMethodId,
          planId: planId,
          coupon: coupon,
          couponAmount: couponAmount,
          couponDurationMonths: couponDurationMonths
        });
      }

      if (response.error) {
        notification.error({
          message: "Payment Error",
          description: response.error,
          duration: 10,
        });
      } else {
        notification.success({
          message: "Payment Success",
          description: "Your payment was successful!",
          duration: 10,
        });
        try {
          const onboardingResponse = await getOnboardingDetails();
          if (onboardingResponse.message === "Onboarding Req") {
            navigate("/onboarding/get-to-know-you", {
              state: { selectedPlan },
            });
          } else if (onboardingResponse.message === "You Can Pass") {
            navigate("/landing-page");
          } else {
            notification.warning({
              message: "Unexpected Response",
              description: "Redirecting to default page.",
              duration: 10,
            });
            navigate("/onboarding/get-to-know-you", {
              state: { selectedPlan },
            });
          }
        } catch (onboardingError) {
          console.error("Error in onboarding API call:", onboardingError);
          notification.error({
            message: "Onboarding Error",
            description:
              "An error occurred during onboarding. Redirecting to default page.",
            duration: 10,
          });
          navigate("/onboarding/get-to-know-you", { state: { selectedPlan } });
        }
      }
    } catch (error) {
      notification.error({
        message: "Payment Error",
        description: error.error,
        duration: 10,
      });
    }

    setLoading(false);
  };

  const handleGoBackButton = () => {
    navigate("/manage-sub", { state: { subscriptionId: subscriptionId } });
  };

  useEffect(() => {
    const fetchAddressData = async () => {
      try {
        const response = await getRequest('/stripe/stripe-address');
        if (response.address) {
          const { line1, city, postal_code, country } = response.address;
          setAddress(line1 || '');
          setPostalCode(postal_code || '');
          setCity(city || '');
          setCountry(country || '');
        }
      } catch (err) {
        console.error('Error fetching address data:', err);
        notification.error({ message: 'Error', description: 'Failed to fetch address data', duration: 10 });
      }
    };

    fetchAddressData();
  }, []);

  const countries = [
    { label: 'Afghanistan', value: 'AF' },
    { label: 'Albania', value: 'AL' },
    { label: 'Algeria', value: 'DZ' },
    { label: 'Andorra', value: 'AD' },
    { label: 'Angola', value: 'AO' },
    { label: 'Antigua and Barbuda', value: 'AG' },
    { label: 'Argentina', value: 'AR' },
    { label: 'Armenia', value: 'AM' },
    { label: 'Australia', value: 'AU' },
    { label: 'Austria', value: 'AT' },
    { label: 'Azerbaijan', value: 'AZ' },
    { label: 'Bahamas', value: 'BS' },
    { label: 'Bahrain', value: 'BH' },
    { label: 'Bangladesh', value: 'BD' },
    { label: 'Barbados', value: 'BB' },
    { label: 'Belarus', value: 'BY' },
    { label: 'Belgium', value: 'BE' },
    { label: 'Belize', value: 'BZ' },
    { label: 'Benin', value: 'BJ' },
    { label: 'Bhutan', value: 'BT' },
    { label: 'Bolivia', value: 'BO' },
    { label: 'Bosnia and Herzegovina', value: 'BA' },
    { label: 'Botswana', value: 'BW' },
    { label: 'Brazil', value: 'BR' },
    { label: 'Brunei', value: 'BN' },
    { label: 'Bulgaria', value: 'BG' },
    { label: 'Burkina Faso', value: 'BF' },
    { label: 'Burundi', value: 'BI' },
    { label: 'Cabo Verde', value: 'CV' },
    { label: 'Cambodia', value: 'KH' },
    { label: 'Cameroon', value: 'CM' },
    { label: 'Canada', value: 'CA' },
    { label: 'Central African Republic', value: 'CF' },
    { label: 'Chad', value: 'TD' },
    { label: 'Chile', value: 'CL' },
    { label: 'China', value: 'CN' },
    { label: 'Colombia', value: 'CO' },
    { label: 'Comoros', value: 'KM' },
    { label: 'Congo', value: 'CG' },
    { label: 'Costa Rica', value: 'CR' },
    { label: 'Croatia', value: 'HR' },
    { label: 'Cuba', value: 'CU' },
    { label: 'Cyprus', value: 'CY' },
    { label: 'Czech Republic', value: 'CZ' },
    { label: 'Denmark', value: 'DK' },
    { label: 'Djibouti', value: 'DJ' },
    { label: 'Dominica', value: 'DM' },
    { label: 'Dominican Republic', value: 'DO' },
    { label: 'DR Congo', value: 'CD' },
    { label: 'Ecuador', value: 'EC' },
    { label: 'Egypt', value: 'EG' },
    { label: 'El Salvador', value: 'SV' },
    { label: 'Equatorial Guinea', value: 'GQ' },
    { label: 'Eritrea', value: 'ER' },
    { label: 'Estonia', value: 'EE' },
    { label: 'Eswatini', value: 'SZ' },
    { label: 'Ethiopia', value: 'ET' },
    { label: 'Fiji', value: 'FJ' },
    { label: 'Finland', value: 'FI' },
    { label: 'France', value: 'FR' },
    { label: 'Gabon', value: 'GA' },
    { label: 'Gambia', value: 'GM' },
    { label: 'Georgia', value: 'GE' },
    { label: 'Germany', value: 'DE' },
    { label: 'Ghana', value: 'GH' },
    { label: 'Greece', value: 'GR' },
    { label: 'Grenada', value: 'GD' },
    { label: 'Guatemala', value: 'GT' },
    { label: 'Guinea', value: 'GN' },
    { label: 'Guinea-Bissau', value: 'GW' },
    { label: 'Guyana', value: 'GY' },
    { label: 'Haiti', value: 'HT' },
    { label: 'Honduras', value: 'HN' },
    { label: 'Hungary', value: 'HU' },
    { label: 'Iceland', value: 'IS' },
    { label: 'India', value: 'IN' },
    { label: 'Indonesia', value: 'ID' },
    { label: 'Iran', value: 'IR' },
    { label: 'Iraq', value: 'IQ' },
    { label: 'Ireland', value: 'IE' },
    { label: 'Israel', value: 'IL' },
    { label: 'Italy', value: 'IT' },
    { label: 'Jamaica', value: 'JM' },
    { label: 'Japan', value: 'JP' },
    { label: 'Jordan', value: 'JO' },
    { label: 'Kazakhstan', value: 'KZ' },
    { label: 'Kenya', value: 'KE' },
    { label: 'Kiribati', value: 'KI' },
    { label: 'Kuwait', value: 'KW' },
    { label: 'Kyrgyzstan', value: 'KG' },
    { label: 'Laos', value: 'LA' },
    { label: 'Latvia', value: 'LV' },
    { label: 'Lebanon', value: 'LB' },
    { label: 'Lesotho', value: 'LS' },
    { label: 'Liberia', value: 'LR' },
    { label: 'Libya', value: 'LY' },
    { label: 'Liechtenstein', value: 'LI' },
    { label: 'Lithuania', value: 'LT' },
    { label: 'Luxembourg', value: 'LU' },
    { label: 'Madagascar', value: 'MG' },
    { label: 'Malawi', value: 'MW' },
    { label: 'Malaysia', value: 'MY' },
    { label: 'Maldives', value: 'MV' },
    { label: 'Mali', value: 'ML' },
    { label: 'Malta', value: 'MT' },
    { label: 'Marshall Islands', value: 'MH' },
    { label: 'Mauritania', value: 'MR' },
    { label: 'Mauritius', value: 'MU' },
    { label: 'Mexico', value: 'MX' },
    { label: 'Micronesia', value: 'FM' },
    { label: 'Moldova', value: 'MD' },
    { label: 'Monaco', value: 'MC' },
    { label: 'Mongolia', value: 'MN' },
    { label: 'Montenegro', value: 'ME' },
    { label: 'Morocco', value: 'MA' },
    { label: 'Mozambique', value: 'MZ' },
    { label: 'Myanmar', value: 'MM' },
    { label: 'Namibia', value: 'NA' },
    { label: 'Nauru', value: 'NR' },
    { label: 'Nepal', value: 'NP' },
    { label: 'Netherlands', value: 'NL' },
    { label: 'New Zealand', value: 'NZ' },
    { label: 'Nicaragua', value: 'NI' },
    { label: 'Niger', value: 'NE' },
    { label: 'Nigeria', value: 'NG' },
    { label: 'North Korea', value: 'KP' },
    { label: 'North Macedonia', value: 'MK' },
    { label: 'Norway', value: 'NO' },
    { label: 'Oman', value: 'OM' },
    { label: 'Pakistan', value: 'PK' },
    { label: 'Palau', value: 'PW' },
    { label: 'Palestine', value: 'PS' },
    { label: 'Panama', value: 'PA' },
    { label: 'Papua New Guinea', value: 'PG' },
    { label: 'Paraguay', value: 'PY' },
    { label: 'Peru', value: 'PE' },
    { label: 'Philippines', value: 'PH' },
    { label: 'Poland', value: 'PL' },
    { label: 'Portugal', value: 'PT' },
    { label: 'Qatar', value: 'QA' },
    { label: 'Romania', value: 'RO' },
    { label: 'Russia', value: 'RU' },
    { label: 'Rwanda', value: 'RW' },
    { label: 'Saint Kitts and Nevis', value: 'KN' },
    { label: 'Saint Lucia', value: 'LC' },
    { label: 'Saint Vincent and the Grenadines', value: 'VC' },
    { label: 'Samoa', value: 'WS' },
    { label: 'San Marino', value: 'SM' },
    { label: 'Sao Tome and Principe', value: 'ST' },
    { label: 'Saudi Arabia', value: 'SA' },
    { label: 'Senegal', value: 'SN' },
    { label: 'Serbia', value: 'RS' },
    { label: 'Seychelles', value: 'SC' },
    { label: 'Sierra Leone', value: 'SL' },
    { label: 'Singapore', value: 'SG' },
    { label: 'Slovakia', value: 'SK' },
    { label: 'Slovenia', value: 'SI' },
    { label: 'Solomon Islands', value: 'SB' },
    { label: 'Somalia', value: 'SO' },
    { label: 'South Africa', value: 'ZA' },
    { label: 'South Korea', value: 'KR' },
    { label: 'South Sudan', value: 'SS' },
    { label: 'Spain', value: 'ES' },
    { label: 'Sri Lanka', value: 'LK' },
    { label: 'Sudan', value: 'SD' },
    { label: 'Suriname', value: 'SR' },
    { label: 'Sweden', value: 'SE' },
    { label: 'Switzerland', value: 'CH' },
    { label: 'Syria', value: 'SY' },
    { label: 'Tajikistan', value: 'TJ' },
    { label: 'Tanzania', value: 'TZ' },
    { label: 'Thailand', value: 'TH' },
    { label: 'Timor-Leste', value: 'TL' },
    { label: 'Togo', value: 'TG' },
    { label: 'Tonga', value: 'TO' },
    { label: 'Trinidad and Tobago', value: 'TT' },
    { label: 'Tunisia', value: 'TN' },
    { label: 'Turkey', value: 'TR' },
    { label: 'Turkmenistan', value: 'TM' },
    { label: 'Tuvalu', value: 'TV' },
    { label: 'Uganda', value: 'UG' },
    { label: 'Ukraine', value: 'UA' },
    { label: 'United Arab Emirates', value: 'AE' },
    { label: 'United Kingdom', value: 'GB' },
    { label: 'United States', value: 'US' },
    { label: 'Uruguay', value: 'UY' },
    { label: 'Uzbekistan', value: 'UZ' },
    { label: 'Vanuatu', value: 'VU' },
    { label: 'Vatican City', value: 'VA' },
    { label: 'Venezuela', value: 'VE' },
    { label: 'Vietnam', value: 'VN' },
    { label: 'Yemen', value: 'YE' },
    { label: 'Zambia', value: 'ZM' },
    { label: 'Zimbabwe', value: 'ZW' }
  ];

  const handleSave = async () => {
    const userId = Number(localStorage.getItem('userid'));

    if (!userId) {
      notification.error({
        message: 'Missing Information',
        description: 'User ID is missing. Please sign in again.',
        duration: 10
      });
      return;
    }

    if (!address.trim()) {
      notification.error({
        message: 'Validation Error',
        description: 'Address is required.',
        duration: 10
      });
      return;
    }

    if (country === "") {
      notification.error({
        message: 'Missing Information',
        description: 'Please select a valid country before submitting.',
        duration: 10,
      });
      return;
    }
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    setModalVisible(false);
    const userId = Number(localStorage.getItem('userid'));

    try {
      const response = await postRequest('/stripe/update-customer-address', {
        address: {
          line1: address,
          city: city,
          postal_code: postalCode,
          country: country
        }
      });

      if (response.message === 'Address updated successfully') {
        notification.success({ message: 'Address Success', description: 'Address updated successfully on stripe!', duration: 10 });

        try {
          const payload = {
            address_street: address,
            city: city,
            postal_code: postalCode,
            country: country
          };
          await updateUser(userId, payload);
          notification.success({ message: 'Profile Updated', description: 'Address updated successfully on profile!', duration: 10 });
        } catch (err) {
          console.error('Error updating user profile:', err.message);
          notification.error({ message: 'Error', description: 'Failed to update user profile', duration: 10 });
        }
      } else {
        notification.error({ message: 'Address Error', description: response.message || 'An error occurred', duration: 10 });
      }
    } catch (err) {
      console.error('Error updating address:', err.message);
      notification.error({ message: 'Error', description: 'Failed to update address', duration: 10 });
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    postRequest('/stripe/update-customer-address', {
      address: {
        line1: address,
        city: city,
        postal_code: postalCode,
        country: country
      }
    }).then(response => {
      if (response.message === 'Address updated successfully') {
        notification.success({ message: 'Address Success', description: 'Address updated successfully on stripe!', duration: 10 });
      } else {
        notification.error({ message: 'Address Error', description: response.message || 'An error occurred', duration: 10 });
      }
    }).catch(err => {
      console.error('Error updating address:', err.message);
      notification.error({ message: 'Error', description: 'Failed to update address', duration: 10 });
    });
  };
  const handleAddNewClick = () => {
    setIsAddingNewCard(true);
  };

  const handleBackClick = () => {
    setIsAddingNewCard(false);
  };

  return (
    <>
      <Col flex="10%" className="go-back-button-subs">
        <button onClick={handleGoBackButton} className="button-styling">
          <img src={goBackButton} alt="Go Back Button" />
        </button>
      </Col>
      {showPopUp && <PopUpInfo onClose={() => setShowPopUp(false)} />}
      <Modal
        title="Confirm Save"
        visible={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Yes"
        cancelText="No"
        style={{ top: 20 }}
        bodyStyle={{ padding: '16px' }}
        footer={[
          <Button
            key="cancel"
            onClick={handleModalCancel}
            style={{ ...styles.modalButton, borderColor: '#d3d3d3', color: '#000' }}
          >
            No
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={handleModalOk}
            style={{
              ...styles.modalButton,
              backgroundColor: '#ff007f',
              borderColor: '#ff007f',
              color: '#fff',
            }}
          >
            Yes
          </Button>
        ]}
      >
        <div style={{ paddingTop: '16px' }}>
          <p>Do you also want to save this address data to your profile?</p>
        </div>
      </Modal>
      <form onSubmit={handleSubmit} style={styles.paymentForm}>
        <h1 className='payment-form-heading'>Set Up Payment</h1>
        <p style={styles.description}>We use Stripe, a safe and efficient online payment processing platform to ensure that your information is secure and protected.</p>
        <div style={styles.formGroup}>
          <label style={styles.labelStyle} htmlFor="address">Billing Address</label>
          <Input
            id="address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Enter your address"
            style={styles.input}
            required
          />
        </div>
        <div style={styles.formGroup}>
          <Input
            id="postal-code"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder="Postal Code"
            style={styles.input}
          />
        </div>
        <div style={{ ...styles.formGroup, ...styles.flexContainer }}>
          <Input
            id="city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            style={{ ...styles.input, ...styles.cityInput }}
            required
          />
          <Select
            id="country"
            value={country}
            onChange={(value) => setCountry(value)}
            style={{ ...styles.cityInput }}
            required
          >
            <Option value="">Select Country</Option>
            {countries.map(({ value, label }) => (
              <Option key={value} value={value}>{label}</Option>
            ))}
          </Select>
        </div>
        <Button
          type="primary"
          onClick={handleSave}
          style={styles.saveButton}
        >
          Save Address
        </Button>
        <div style={styles.formGroup}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={styles.labelStyle} htmlFor="payment-method">Choose your payment method</label>
            <QuestionCircleOutlined 
              onClick={() => setShowPopUp(true)}
              style={{ marginLeft: '8px', color: '#000', cursor: 'pointer', backgroundColor: '#ff4081' }}
            />
          </div>
          <Select
            id="payment-method"
            value={paymentMethod}
            onChange={setPaymentMethod}
            style={{ width: '100%' }}
          >
            <Option value="card">Credit or Debit Card</Option>
          </Select>
        </div>
         {(isAddingNewCard || !defaultCard) && (
          <div style={styles.formGroup}>
            <label style={styles.labelStyle} htmlFor="card-owner">Card Owner Name</label>
            <Input
              id="card-owner"
              onChange={(e) => setCardOwnerName(e.target.value)}
              placeholder="Enter card owner's name"
              style={styles.input}
            />
          </div>
        )}
        <div style={styles.formGroup}>
          <label style={styles.labelStyle} htmlFor="card-details">
            {isAddingNewCard || !defaultCard
              ? "Card Details"
              : "Your Default Card Details"}
          </label>

          {isAddingNewCard || !defaultCard ? (
            <CardElement id="card-details" className="stripe-element" />
          ) : (
            <Row justify={"center"} className="my-2">
              <img
                style={{ height: "30px", width: "50px" }}
                src={
                  defaultCard?.defaultPaymentMethod?.brand.toLowerCase() ===
                    "visa"
                    ? VisaLogo
                    : MasterCardLogo
                }
                alt={defaultCard?.defaultPaymentMethod?.brand}
              />
              <span className="card-details">{`${defaultCard.defaultPaymentMethod.brand.charAt(0).toUpperCase() +
                defaultCard.defaultPaymentMethod.brand.slice(1)
                } - ${defaultCard.defaultPaymentMethod.last4}`} { cardOwnerName && `- ${cardOwnerName}` }</span>
            </Row>
          )}

          <Row gutter={16} style={{ marginTop: "16px" }}>
            {isAddingNewCard && defaultCard ? (
              <>
                <Col span={12}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={styles.button}
                    disabled={!stripe || loading}
                  >
                    {loading ? "Processing..." : "Add"}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    type="default"
                    style={styles.button}
                    onClick={handleBackClick}
                  >
                    Back
                  </Button>
                </Col>
              </>
            ) : defaultCard ? (
              <>
                <Col span={12}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={styles.button}
                    disabled={!stripe || loading}
                  >
                    {loading ? "Processing..." : "Confirm"}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    type="default"
                    style={styles.button}
                    onClick={handleAddNewClick}
                  >
                    Add New
                  </Button>
                </Col>
              </>
            ) : (
              <Col span={24}>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={styles.button}
                  disabled={!stripe || loading}
                >
                  {loading ? "Processing..." : "Add"}
                </Button>
              </Col>
            )}
          </Row>
        </div>
      </form>
    </>
  );
};

export default PaymentForm;
